import React, { useState } from 'react';
import { 
  Server, Database, Shield, Globe, Cpu, HardDrive,
  Radio, Activity, Layers, MonitorCheck,
  ZoomIn, ZoomOut, Maximize
} from 'lucide-react';

// Constantes pour les dimensions
const GRID_SIZE = 20;
const COMPONENT_WIDTH = GRID_SIZE * 12;
const COMPONENT_HEIGHT = GRID_SIZE * 8;
const ZOOM_STEP = 0.1;
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 2;

// Component type definitions with icons and colors
const COMPONENT_TYPES = {
  PROXY: {
    icon: Shield,
    color: '#3B82F6',
    label: 'Proxy Server'
  },
  SERVER: {
    icon: Server,
    color: '#10B981',
    label: 'Application Server'
  },
  DATABASE: {
    icon: Database,
    color: '#6366F1',
    label: 'Database Server'
  },
  CACHE: {
    icon: HardDrive,
    color: '#F59E0B',
    label: 'Cache Server'
  },
  QUEUE: {
    icon: Radio,
    color: '#EC4899',
    label: 'Message Queue'
  }
};

// Connection type definitions with styles and animations
const CONNECTION_TYPES = {
  HTTP: {
    name: 'HTTP/HTTPS',
    color: '#3B82F6',
    pattern: [1, 0],
    icon: Globe,
    label: 'Web Traffic',
    animationDuration: '10s'
  },
  TCP: {
    name: 'TCP/IP',
    color: '#10B981',
    pattern: [5, 5],
    icon: Activity,
    label: 'Database Connection',
    animationDuration: '8s'
  },
  UDP: {
    name: 'UDP',
    color: '#F59E0B',
    pattern: [2, 2],
    icon: Layers,
    label: 'Fast Data Transfer',
    animationDuration: '6s'
  }
};

// Infrastructure configuration
const INFRASTRUCTURE_CONFIG = {
  components: {
    proxy1: {
      id: 'proxy1',
      name: 'Main Proxy',
      hostname: 'proxy-01.dmz',
      type: 'PROXY',
      x: GRID_SIZE * 15,
      y: GRID_SIZE * 1,
      zone: 'dmz',
      status: 'active',
      ip: '10.0.0.1',
      description: 'Main entry point proxy',
      software: [
        { name: 'NGINX', version: '1.20.2', status: 'running' }
        
      ],
      specs: {
        cpu: '2 vCPU',
        memory: '4GB'
      },
      connections: [
        {
          target: 'app1',
          type: 'HTTP',
          status: 'active',
          points: []
        }
      ]
    },
    app1: {
      id: 'app1',
      name: 'Application Server',
      hostname: 'app-01.internal',
      type: 'SERVER',
      x: GRID_SIZE * 20,
      y: GRID_SIZE * 15,
      zone: 'private_cloud',
      status: 'active',
      ip: '10.0.0.2',
      software: [
        { name: 'Node.js', version: '18.x', status: 'running' },
        { name: 'PM2', version: '5.2.0', status: 'running' },
        { name: 'Express', version: '4.18.2', status: 'running' }
      ],
      specs: {
        cpu: '4 vCPU',
        memory: '8GB'
      },
      connections: [
        {
          target: 'db1',
          type: 'TCP',
          status: 'active',
          points: []
        }
      ]
    },
	app2: {
      id: 'app1',
      name: 'Application Server',
      hostname: 'app-01.internal',
      type: 'SERVER',
      x: GRID_SIZE * 40,
      y: GRID_SIZE * 15,
      zone: 'private_cloud',
      status: 'active',
      ip: '10.0.0.2',
      software: [
        { name: 'Node.js', version: '18.x', status: 'running' },
        { name: 'PM2', version: '5.2.0', status: 'running' },
        { name: 'Express', version: '4.18.2', status: 'running' }
      ],
      specs: {
        cpu: '4 vCPU',
        memory: '8GB'
      },
      connections: [
        {
          target: 'db1',
          type: 'TCP',
          status: 'active',
          points: []
        }
      ]
    },
    db1: {
      id: 'db1',
      name: 'Primary Database',
      hostname: 'db-01.internal',
      type: 'DATABASE',
      x: GRID_SIZE * 20,
      y: GRID_SIZE * 35,
      zone: 'private_cloud',
      status: 'active',
      ip: '10.0.0.3',
      software: [
        { name: 'PostgreSQL', version: '14.5', status: 'running' },
        { name: 'TimescaleDB', version: '2.8', status: 'running' }
      ],
      specs: {
        cpu: '8 vCPU',
        memory: '16GB'
      }
    }
  }
};

// Component for individual infrastructure elements
const InfraComponent = ({ component }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    type,
    name,
    hostname,
    status,
    ip,
    specs,
    software = [],
    x,
    y
  } = component;
  
  const componentType = COMPONENT_TYPES[type];
  const Icon = componentType?.icon || Server;

  return (
    <div
      className="absolute transition-transform duration-200 ease-in-out"
      style={{
        transform: `translate(${x}px, ${y}px)`,
        width: COMPONENT_WIDTH,
        height: isExpanded ? 'auto' : COMPONENT_HEIGHT,
        zIndex: isExpanded ? 50 : 1
      }}
    >
      <div 
        className={`relative w-full h-full p-4 rounded-xl border-2 
          ${status === 'active' ? 'border-green-300' : 'border-gray-300'}
          bg-white shadow-lg hover:shadow-xl transition-shadow duration-200
          ${isExpanded ? 'ring-2 ring-blue-400' : ''}
        `}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3 mb-3">
          <Icon 
            className="w-6 h-6"
            style={{ color: componentType?.color }}
          />
          <div>
            <h3 className="font-semibold text-gray-800">{name}</h3>
            <p className="text-xs text-gray-500">{hostname}</p>
          </div>
          <span className={`
            ml-auto px-2 py-1 text-xs rounded-full font-medium
            ${status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
          `}>
            {status}
          </span>
        </div>

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-400" />
            <span className="text-gray-600">{ip}</span>
          </div>
          
          {specs && (
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(specs).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <MonitorCheck className="w-4 h-4 text-gray-400" />
                  <span className="text-gray-600">
                    {key}: <span className="font-medium">{value}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {isExpanded && software.length > 0 && (
            <div className="mt-4 border-t pt-3">
              <h4 className="font-medium text-gray-700 mb-2">Running Software</h4>
              <div className="space-y-2">
                {software.map((sw, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <div className="flex items-center gap-2">
                      <Layers className="w-4 h-4 text-gray-500" />
                      <span className="text-gray-700">{sw.name}</span>
                      <span className="text-xs text-gray-500">{sw.version}</span>
                    </div>
                    <span className={`
                      px-2 py-0.5 text-xs rounded-full
                      ${sw.status === 'running' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}
                    `}>
                      {sw.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const ZoomControls = ({ zoom, setZoom }) => {
  const handleZoomIn = () => setZoom(prev => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  const handleReset = () => setZoom(1);

  return (
    <div className="fixed bottom-4 right-4 flex gap-2 bg-white p-2 rounded-lg shadow-lg z-50">
      <button
        onClick={handleZoomIn}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Zoom In"
      >
        <ZoomIn size={20} />
      </button>
      <button
        onClick={handleZoomOut}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Zoom Out"
      >
        <ZoomOut size={20} />
      </button>
      <button
        onClick={handleReset}
        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        title="Reset Zoom"
      >
        <Maximize size={20} />
      </button>
      <span className="flex items-center px-2 text-sm text-gray-600">
        {Math.round(zoom * 100)}%
      </span>
    </div>
  );
};

const Grid = ({ zoom }) => {
  const gridSize = GRID_SIZE * zoom;
  return (
    <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
      <defs>
        <pattern 
          id="grid" 
          width={gridSize} 
          height={gridSize} 
          patternUnits="userSpaceOnUse"
        >
          <path 
            d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} 
            fill="none" 
            stroke="rgba(0,0,0,0.1)" 
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#grid)" />
    </svg>
  );
};


// Component for connection lines between infrastructure components
const ConnectionLine = ({ source, target, points, type }) => {
  const [isHovered, setIsHovered] = useState(false);
  const connectionStyle = CONNECTION_TYPES[type];
  
  const startX = source.x + COMPONENT_WIDTH / 2;
  const startY = source.y + COMPONENT_HEIGHT / 2;
  const targetComponent = INFRASTRUCTURE_CONFIG.components[target];
  const endX = targetComponent.x + COMPONENT_WIDTH / 2;
  const endY = targetComponent.y + COMPONENT_HEIGHT / 2;
  
  const pathId = `path-${source.id}-${target}`;
  const ConnectionIcon = connectionStyle.icon;

  const createPath = () => {
    let path = `M ${startX} ${startY}`;
    if (points && points.length > 0) {
      points.forEach(point => path += ` L ${point[0]} ${point[1]}`);
    }
    path += ` L ${endX} ${endY}`;
    return path;
  };

  const pathD = createPath();
  const midX = (startX + endX) / 2;
  const midY = (startY + endY) / 2;

  return (
    <g
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="cursor-pointer"
      style={{ pointerEvents: 'all' }}
    >
      <path
        id={pathId}
        d={pathD}
        stroke={connectionStyle.color}
        strokeWidth={isHovered ? 3 : 2}
        fill="none"
        strokeDasharray={connectionStyle.pattern}
        opacity={isHovered ? "0.8" : "0.5"}
      />
      
      {/* Animation de flux */}
      {[0, 1, 2].map((i) => (
        <circle 
          key={i}
          r="3" 
          fill={connectionStyle.color}
        >
          <animateMotion
            dur={connectionStyle.animationDuration}
            repeatCount="indefinite"
            path={pathD}
            begin={`-${(parseFloat(connectionStyle.animationDuration) * i) / 3}s`}
          />
        </circle>
      ))}

      <circle cx={startX} cy={startY} r="4" fill={connectionStyle.color} />
      <circle cx={endX} cy={endY} r="4" fill={connectionStyle.color} />

      <g transform={`translate(${midX - 12}, ${midY - 12})`}>
        <rect
          width="24"
          height="24"
          fill="white"
          stroke={connectionStyle.color}
          strokeWidth="2"
          rx="6"
          className={`${isHovered ? 'filter drop-shadow-md' : ''}`}
        />
        <foreignObject width="24" height="24">
          <div className="w-full h-full flex items-center justify-center">
            <ConnectionIcon 
              className="w-4 h-4" 
              style={{ color: connectionStyle.color }} 
            />
          </div>
        </foreignObject>
      </g>

      {isHovered && (
        <g transform={`translate(${midX}, ${midY - 30})`}>
          <rect
            x="-70"
            y="-35"
            width="140"
            height="70"
            fill="white"
            stroke={connectionStyle.color}
            strokeWidth="1"
            rx="4"
            className="filter drop-shadow-lg"
          />
          <text
            textAnchor="middle"
            y="-15"
            className="text-xs font-medium fill-gray-700"
          >
            {connectionStyle.name}
          </text>
          <text
            textAnchor="middle"
            y="5"
            className="text-xs fill-gray-500"
          >
            {connectionStyle.label}
          </text>
          <text
            textAnchor="middle"
            y="25"
            className="text-xs fill-gray-500"
          >
            {`${source.hostname} → ${targetComponent.hostname}`}
          </text>
        </g>
      )}
    </g>
  );
};

// Main component that renders the infrastructure grid
const InfrastructureGrid = () => {
  const [zoom, setZoom] = useState(1);
  const { components } = INFRASTRUCTURE_CONFIG;

  // Style for the zoomed content
  const contentStyle = {
    transform: `scale(${zoom})`,
    transformOrigin: '0 0',
    width: `${100 / zoom}%`,
    height: `${100 / zoom}%`
  };

  return (
    <div className="relative w-full h-screen bg-gray-50 overflow-auto p-8">
      {/* Grid Background */}
      <Grid zoom={zoom} />

      {/* Zoom Controls */}
      <ZoomControls zoom={zoom} setZoom={setZoom} />

      {/* Zoomed Content Container */}
      <div style={contentStyle}>
        {/* SVG Connections Layer */}
        <svg 
          className="absolute inset-0 w-full h-full"
          style={{ pointerEvents: 'none' }}
        >
          <g style={{ pointerEvents: 'all' }}>
            {Object.values(components).map(component => 
              component.connections?.map((conn, idx) => (
                <ConnectionLine
                  key={`${component.id}-${conn.target}-${idx}`}
                  source={component}
                  target={conn.target}
                  points={conn.points}
                  type={conn.type}
                />
              ))
            )}
          </g>
        </svg>

        {/* Infrastructure Components */}
        {Object.values(components).map(component => (
          <InfraComponent
            key={component.id}
            component={component}
          />
        ))}
      </div>
    </div>
  );
};
export default InfrastructureGrid;